import React, { useState, useEffect, useCallback } from 'react';
import styles from './ProjectWebsiteModule.module.css';
import { Project, PublicWebsiteSettings } from '../../types';
import { publicWebsiteAPI, projectsAPI } from '../../services/api';

interface ProjectWebsiteModuleProps {
  project: Project;
}

const DEFAULT_SECTIONS = {
  hero:             { enabled: true,  order: 0 },
  about:            { enabled: true,  order: 1 },
  gebiedsanalyse:   { enabled: false, order: 2 },
  facts:            { enabled: false, order: 3 },
  timeline:         { enabled: true,  order: 4 },
  media:            { enabled: false, order: 5 },
  participation:    { enabled: false, order: 6 },
  faq:              { enabled: true,  order: 7 },
  contact:          { enabled: true,  order: 8 },
  newsletter:       { enabled: false, order: 9 },
};

const SECTION_LABELS: Record<string, string> = {
  hero: 'Hero / Banner',
  about: 'Over het project',
  gebiedsanalyse: 'Gebiedsanalyse',
  facts: 'Feiten & Cijfers',
  timeline: 'Tijdlijn',
  media: 'Media galerij',
  participation: 'Participatie',
  faq: 'Veelgestelde vragen',
  contact: 'Contact',
  newsletter: 'Nieuwsbrief',
};

const SECTION_DESCRIPTIONS: Record<string, string> = {
  hero: 'Grote banner bovenaan met titel, subtitel en achtergrondafbeelding',
  about: 'Introductietekst over het project en locatie-informatie',
  gebiedsanalyse: 'Interactieve kaart met demografische data en stakeholder-adviezen uit de Gebiedsanalyse-module',
  facts: 'Geanimeerde kerngetallen en statistieken',
  timeline: 'Tijdlijn met milestones uit de werkplanning',
  media: 'Galerij met publiek gedeelde media-items',
  participation: 'Informatie over participatiemogelijkheden en events',
  faq: 'Veelgestelde vragen uit de Vragen-module (publiek gemarkeerd)',
  contact: 'Contactgegevens en contactformulier',
  newsletter: 'Nieuwsbrief aanmeldformulier',
};

const defaultSettings: PublicWebsiteSettings = {
  enabled: false,
  slug: '',
  hero: { ctaText: 'Lees meer', ctaLink: '#over', stats: [] },
  sections: DEFAULT_SECTIONS,
  about: { showLocation: true },
  facts: [],
  contact: { heading: 'Neem contact met ons op', showForm: true },
  participation: { heading: 'Denk en doe mee', events: [] },
  newsletter: { heading: 'Blijf op de hoogte' },
  theme: { primaryColor: '#E6007E', headerStyle: 'dark' },
  seo: {},
};

export const ProjectWebsiteModule: React.FC<ProjectWebsiteModuleProps> = ({ project }) => {
  const [settings, setSettings] = useState<PublicWebsiteSettings>(defaultSettings);
  const [activeTab, setActiveTab] = useState<'general' | 'content' | 'sections' | 'analytics' | 'preview'>('general');
  const [analyticsData, setAnalyticsData] = useState<any>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsPeriod, setAnalyticsPeriod] = useState<'7d' | '30d' | '90d'>('30d');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
  const [slugChecking, setSlugChecking] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  useEffect(() => {
    loadSettings();
  }, [project.id]);

  const loadSettings = async () => {
    try {
      const data = await publicWebsiteAPI.getSettings(project.id);
      if (data.publicWebsite && Object.keys(data.publicWebsite).length > 0) {
        setSettings({ ...defaultSettings, ...data.publicWebsite });
      }
    } catch (error) {
      console.error('Fout bij laden website-instellingen:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const data = await publicWebsiteAPI.getAnalytics(project.id);
      setAnalyticsData(data);
    } catch (error) {
      console.error('Fout bij laden analytics:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadSubscribers = async () => {
    setSubscribersLoading(true);
    try {
      const data = await publicWebsiteAPI.getSubscribers(project.id);
      setSubscribers(data.subscribers || []);
    } catch (error) {
      console.error('Fout bij laden abonnees:', error);
    } finally {
      setSubscribersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
      loadSubscribers();
    }
  }, [activeTab, project.id]);

  const getFilteredDailyViews = () => {
    if (!analyticsData?.analytics?.dailyViews) return [];
    const days = analyticsPeriod === '7d' ? 7 : analyticsPeriod === '30d' ? 30 : 90;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    const cutoffStr = cutoff.toISOString().split('T')[0];
    return analyticsData.analytics.dailyViews.filter((d: any) => d.date >= cutoffStr);
  };

  const getMaxViews = (views: any[]) => {
    if (views.length === 0) return 1;
    return Math.max(...views.map((d: any) => d.views), 1);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMessage(null);
    try {
      const updated = await publicWebsiteAPI.updateSettings(project.id, settings);
      setSettings({ ...defaultSettings, ...updated });
      setSaveMessage('Instellingen opgeslagen');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (error: any) {
      setSaveMessage(error?.response?.data?.error || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const checkSlug = useCallback(async (slug: string) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    setSlugChecking(true);
    try {
      const available = await publicWebsiteAPI.checkSlug(project.id, slug);
      setSlugAvailable(available);
    } catch {
      setSlugAvailable(null);
    } finally {
      setSlugChecking(false);
    }
  }, [project.id]);

  const handleSlugChange = (value: string) => {
    const slug = value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-');
    setSettings(prev => ({ ...prev, slug }));
    // Debounce slug check
    const timer = setTimeout(() => checkSlug(slug), 500);
    return () => clearTimeout(timer);
  };

  const updateSettings = (path: string, value: any) => {
    setSettings(prev => {
      const updated = { ...prev };
      const keys = path.split('.');
      let current: any = updated;
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      return updated;
    });
  };

  const toggleSection = (sectionId: string) => {
    setSettings(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionId]: {
          ...prev.sections[sectionId as keyof typeof prev.sections],
          enabled: !prev.sections[sectionId as keyof typeof prev.sections]?.enabled,
        },
      },
    }));
  };

  const addFact = () => {
    setSettings(prev => ({
      ...prev,
      facts: [...(prev.facts || []), { value: '', label: '', suffix: '' }],
    }));
  };

  const removeFact = (index: number) => {
    setSettings(prev => ({
      ...prev,
      facts: prev.facts.filter((_, i) => i !== index),
    }));
  };

  const updateFact = (index: number, field: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      facts: prev.facts.map((f, i) => i === index ? { ...f, [field]: value } : f),
    }));
  };

  const addHeroStat = () => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        stats: [...(prev.hero.stats || []), { value: '', label: '' }],
      },
    }));
  };

  const removeHeroStat = (index: number) => {
    setSettings(prev => ({
      ...prev,
      hero: {
        ...prev.hero,
        stats: (prev.hero.stats || []).filter((_, i) => i !== index),
      },
    }));
  };

  const addParticipationEvent = () => {
    setSettings(prev => ({
      ...prev,
      participation: {
        ...prev.participation,
        events: [...(prev.participation.events || []), { title: '', date: '', location: '' }],
      },
    }));
  };

  const removeParticipationEvent = (index: number) => {
    setSettings(prev => ({
      ...prev,
      participation: {
        ...prev.participation,
        events: (prev.participation.events || []).filter((_, i) => i !== index),
      },
    }));
  };

  const [subscribers, setSubscribers] = useState<Array<{ email: string; subscribedAt: string }>>([]);
  const [subscribersLoading, setSubscribersLoading] = useState(false);
  const [uploadingSection, setUploadingSection] = useState<string | null>(null);

  const handleSectionImageUpload = async (section: string, file: File) => {
    setUploadingSection(section);
    try {
      const result = await projectsAPI.uploadSectionImage(project.id, section, file);
      if (section === 'facts') {
        updateSettings('factsImage', result.imageUrl);
      } else {
        updateSettings(`${section}.image`, result.imageUrl);
      }
    } catch (error: any) {
      console.error('Upload mislukt:', error);
      const msg = error.response?.data?.error || error.message || 'Upload mislukt';
      alert(`Upload mislukt: ${msg}`);
    } finally {
      setUploadingSection(null);
    }
  };

  const removeSectionImage = (section: string) => {
    if (section === 'facts') {
      updateSettings('factsImage', '');
    } else {
      updateSettings(`${section}.image`, '');
    }
  };

  const renderImageUpload = (section: string, currentImage?: string) => (
    <div className={styles.formGroup}>
      <label className={styles.label}>Afbeelding</label>
      {currentImage ? (
        <div className={styles.sectionImagePreview}>
          <img src={currentImage} alt={`${section} afbeelding`} />
          <button
            type="button"
            className={styles.removeImageBtn}
            onClick={() => removeSectionImage(section)}
          >
            Verwijderen
          </button>
        </div>
      ) : (
        <div className={styles.imageUploadArea}>
          <input
            type="file"
            accept="image/*"
            id={`section-image-${section}`}
            className={styles.fileInput}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleSectionImageUpload(section, file);
            }}
          />
          <label htmlFor={`section-image-${section}`} className={styles.imageUploadLabel}>
            {uploadingSection === section ? 'Uploaden...' : (
              <>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <circle cx="8.5" cy="8.5" r="1.5" />
                  <polyline points="21 15 16 10 5 21" />
                </svg>
                Afbeelding uploaden
              </>
            )}
          </label>
          <small>Max 10MB. JPEG, PNG, GIF, WebP.</small>
        </div>
      )}
    </div>
  );

  const siteUrl = settings.slug ? `${window.location.origin}/site/${settings.slug}` : '';

  if (loading) {
    return <div className={styles.loading}>Laden...</div>;
  }

  return (
    <div className={styles.module}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>Projectwebsite</h1>
          <p className={styles.subtitle}>Beheer de publieke website voor dit project</p>
        </div>
        <div className={styles.headerRight}>
          <div className={styles.enableToggle}>
            <label className={styles.toggleLabel}>
              <span className={styles.toggleText}>
                {settings.enabled ? 'Website actief' : 'Website inactief'}
              </span>
              <div
                className={`${styles.toggle} ${settings.enabled ? styles.toggleActive : ''}`}
                onClick={() => updateSettings('enabled', !settings.enabled)}
              >
                <div className={styles.toggleKnob} />
              </div>
            </label>
          </div>
          <button
            className={styles.saveButton}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Opslaan...' : 'Opslaan'}
          </button>
        </div>
      </div>

      {saveMessage && (
        <div className={`${styles.saveMessage} ${saveMessage.includes('Fout') ? styles.saveError : styles.saveSuccess}`}>
          {saveMessage}
        </div>
      )}

      {settings.enabled && siteUrl && (
        <div className={styles.liveUrl}>
          <span className={styles.liveIndicator} />
          <span>Live op: </span>
          <a href={siteUrl} target="_blank" rel="noopener noreferrer">{siteUrl}</a>
          {settings.analytics?.totalViews !== undefined && (
            <span className={styles.viewCount}>{settings.analytics.totalViews} bezoeken</span>
          )}
        </div>
      )}

      <div className={styles.tabs}>
        {(['general', 'content', 'sections', 'analytics', 'preview'] as const).map(tab => (
          <button
            key={tab}
            className={`${styles.tab} ${activeTab === tab ? styles.tabActive : ''}`}
            onClick={() => setActiveTab(tab)}
          >
            {tab === 'general' ? 'Algemeen' : tab === 'content' ? 'Inhoud' : tab === 'sections' ? 'Secties' : tab === 'analytics' ? 'Analytics' : 'Preview'}
          </button>
        ))}
      </div>

      <div className={styles.tabContent}>
        {activeTab === 'general' && (
          <div className={styles.tabPanel}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Website URL</h3>
              <div className={styles.slugInput}>
                <span className={styles.slugPrefix}>{window.location.origin}/site/</span>
                <input
                  type="text"
                  value={settings.slug || ''}
                  onChange={e => handleSlugChange(e.target.value)}
                  placeholder="mijn-project"
                  className={styles.input}
                />
                {slugChecking && <span className={styles.slugStatus}>Controleren...</span>}
                {!slugChecking && slugAvailable === true && <span className={styles.slugAvailable}>Beschikbaar</span>}
                {!slugChecking && slugAvailable === false && <span className={styles.slugTaken}>Al in gebruik</span>}
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>SEO Instellingen</h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>Paginatitel</label>
                <input
                  type="text"
                  value={settings.seo?.metaTitle || ''}
                  onChange={e => updateSettings('seo.metaTitle', e.target.value)}
                  placeholder={project.name}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Meta beschrijving</label>
                <textarea
                  value={settings.seo?.metaDescription || ''}
                  onChange={e => updateSettings('seo.metaDescription', e.target.value)}
                  placeholder={project.description}
                  className={styles.textarea}
                  rows={3}
                />
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Thema</h3>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Primaire kleur</label>
                  <div className={styles.colorInput}>
                    <input
                      type="color"
                      value={settings.theme?.primaryColor || '#E6007E'}
                      onChange={e => updateSettings('theme.primaryColor', e.target.value)}
                      className={styles.colorPicker}
                    />
                    <input
                      type="text"
                      value={settings.theme?.primaryColor || '#E6007E'}
                      onChange={e => updateSettings('theme.primaryColor', e.target.value)}
                      className={styles.input}
                    />
                  </div>
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Header stijl</label>
                  <select
                    value={settings.theme?.headerStyle || 'dark'}
                    onChange={e => updateSettings('theme.headerStyle', e.target.value)}
                    className={styles.select}
                  >
                    <option value="dark">Donker</option>
                    <option value="light">Licht</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'content' && (
          <div className={styles.tabPanel}>
            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Hero Sectie</h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>Titel</label>
                <input
                  type="text"
                  value={settings.hero?.title || ''}
                  onChange={e => updateSettings('hero.title', e.target.value)}
                  placeholder={project.name}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Subtitel</label>
                <input
                  type="text"
                  value={settings.hero?.subtitle || ''}
                  onChange={e => updateSettings('hero.subtitle', e.target.value)}
                  placeholder={project.description}
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>CTA knop tekst</label>
                <input
                  type="text"
                  value={settings.hero?.ctaText || 'Lees meer'}
                  onChange={e => updateSettings('hero.ctaText', e.target.value)}
                  className={styles.input}
                />
              </div>

              <div className={styles.subSection}>
                <h4>Kerngetallen in hero</h4>
                {(settings.hero?.stats || []).map((stat, i) => (
                  <div key={i} className={styles.inlineRow}>
                    <input
                      type="text"
                      value={stat.value}
                      onChange={e => {
                        const newStats = [...(settings.hero?.stats || [])];
                        newStats[i] = { ...newStats[i], value: e.target.value };
                        updateSettings('hero.stats', newStats);
                      }}
                      placeholder="bijv. 500"
                      className={styles.inputSmall}
                    />
                    <input
                      type="text"
                      value={stat.label}
                      onChange={e => {
                        const newStats = [...(settings.hero?.stats || [])];
                        newStats[i] = { ...newStats[i], label: e.target.value };
                        updateSettings('hero.stats', newStats);
                      }}
                      placeholder="bijv. Woningen"
                      className={styles.input}
                    />
                    <button className={styles.removeButton} onClick={() => removeHeroStat(i)}>Verwijder</button>
                  </div>
                ))}
                <button className={styles.addButton} onClick={addHeroStat}>+ Kerngetal toevoegen</button>
              </div>
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Over het project</h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>Heading</label>
                <input
                  type="text"
                  value={settings.about?.heading || ''}
                  onChange={e => updateSettings('about.heading', e.target.value)}
                  placeholder="Over het project"
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tekst</label>
                <textarea
                  value={settings.about?.body || ''}
                  onChange={e => updateSettings('about.body', e.target.value)}
                  placeholder="Beschrijf het project voor het publiek..."
                  className={styles.textarea}
                  rows={6}
                />
              </div>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings.about?.showLocation ?? true}
                  onChange={e => updateSettings('about.showLocation', e.target.checked)}
                />
                Locatie tonen
              </label>
              {renderImageUpload('about', settings.about?.image)}
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Feiten & Cijfers</h3>
              {(settings.facts || []).map((fact, i) => (
                <div key={i} className={styles.inlineRow}>
                  <input
                    type="text"
                    value={fact.value}
                    onChange={e => updateFact(i, 'value', e.target.value)}
                    placeholder="bijv. 2500"
                    className={styles.inputSmall}
                  />
                  <input
                    type="text"
                    value={fact.label}
                    onChange={e => updateFact(i, 'label', e.target.value)}
                    placeholder="bijv. Nieuwe woningen"
                    className={styles.input}
                  />
                  <input
                    type="text"
                    value={fact.suffix || ''}
                    onChange={e => updateFact(i, 'suffix', e.target.value)}
                    placeholder="bijv. MW"
                    className={styles.inputSmall}
                  />
                  <button className={styles.removeButton} onClick={() => removeFact(i)}>Verwijder</button>
                </div>
              ))}
              <button className={styles.addButton} onClick={addFact}>+ Feit toevoegen</button>
              {renderImageUpload('facts', (settings as any).factsImage)}
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Contact</h3>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Naam contactpersoon</label>
                  <input
                    type="text"
                    value={settings.contact?.contactPerson?.name || ''}
                    onChange={e => updateSettings('contact.contactPerson.name', e.target.value)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Functie</label>
                  <input
                    type="text"
                    value={settings.contact?.contactPerson?.role || ''}
                    onChange={e => updateSettings('contact.contactPerson.role', e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>
              <div className={styles.formRow}>
                <div className={styles.formGroup}>
                  <label className={styles.label}>E-mail</label>
                  <input
                    type="email"
                    value={settings.contact?.contactPerson?.email || ''}
                    onChange={e => updateSettings('contact.contactPerson.email', e.target.value)}
                    className={styles.input}
                  />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Telefoon</label>
                  <input
                    type="tel"
                    value={settings.contact?.contactPerson?.phone || ''}
                    onChange={e => updateSettings('contact.contactPerson.phone', e.target.value)}
                    className={styles.input}
                  />
                </div>
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>E-mail voor formulierinzendingen</label>
                <input
                  type="email"
                  value={settings.contactEmail || ''}
                  onChange={e => updateSettings('contactEmail', e.target.value)}
                  placeholder={settings.contact?.contactPerson?.email || 'Standaard: e-mail contactpersoon'}
                  className={styles.input}
                />
                <small style={{ color: '#888', marginTop: 4, display: 'block' }}>
                  Contactformulier-berichten worden naar dit adres gestuurd. Laat leeg om het e-mailadres van de contactpersoon te gebruiken.
                </small>
              </div>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={settings.contact?.showForm ?? true}
                  onChange={e => updateSettings('contact.showForm', e.target.checked)}
                />
                Contactformulier tonen
              </label>
              {renderImageUpload('contact', settings.contact?.image)}
            </div>

            <div className={styles.section}>
              <h3 className={styles.sectionTitle}>Participatie</h3>
              <div className={styles.formGroup}>
                <label className={styles.label}>Heading</label>
                <input
                  type="text"
                  value={settings.participation?.heading || ''}
                  onChange={e => updateSettings('participation.heading', e.target.value)}
                  placeholder="Denk en doe mee"
                  className={styles.input}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Tekst</label>
                <textarea
                  value={settings.participation?.body || ''}
                  onChange={e => updateSettings('participation.body', e.target.value)}
                  placeholder="Beschrijf de participatiemogelijkheden..."
                  className={styles.textarea}
                  rows={4}
                />
              </div>
              <div className={styles.subSection}>
                <h4>Events</h4>
                {(settings.participation?.events || []).map((event, i) => (
                  <div key={i} className={styles.eventRow}>
                    <input
                      type="text"
                      value={event.title}
                      onChange={e => {
                        const events = [...(settings.participation?.events || [])];
                        events[i] = { ...events[i], title: e.target.value };
                        updateSettings('participation.events', events);
                      }}
                      placeholder="Titel"
                      className={styles.input}
                    />
                    <input
                      type="date"
                      value={event.date ? event.date.split('T')[0] : ''}
                      onChange={e => {
                        const events = [...(settings.participation?.events || [])];
                        events[i] = { ...events[i], date: e.target.value };
                        updateSettings('participation.events', events);
                      }}
                      className={styles.inputSmall}
                    />
                    <input
                      type="text"
                      value={event.location}
                      onChange={e => {
                        const events = [...(settings.participation?.events || [])];
                        events[i] = { ...events[i], location: e.target.value };
                        updateSettings('participation.events', events);
                      }}
                      placeholder="Locatie"
                      className={styles.input}
                    />
                    <button className={styles.removeButton} onClick={() => removeParticipationEvent(i)}>Verwijder</button>
                  </div>
                ))}
                <button className={styles.addButton} onClick={addParticipationEvent}>+ Event toevoegen</button>
              </div>
              {renderImageUpload('participation', settings.participation?.image)}
            </div>
          </div>
        )}

        {activeTab === 'sections' && (
          <div className={styles.tabPanel}>
            <p className={styles.sectionHint}>
              Kies welke secties zichtbaar zijn op de publieke website. Secties worden in de aangegeven volgorde getoond.
            </p>
            <div className={styles.sectionsList}>
              {Object.entries(SECTION_LABELS)
                .sort(([a], [b]) => {
                  const orderA = settings.sections?.[a as keyof typeof settings.sections]?.order ?? 99;
                  const orderB = settings.sections?.[b as keyof typeof settings.sections]?.order ?? 99;
                  return orderA - orderB;
                })
                .map(([id, label]) => {
                  const section = settings.sections?.[id as keyof typeof settings.sections];
                  const isEnabled = section?.enabled ?? false;
                  return (
                    <div
                      key={id}
                      className={`${styles.sectionItem} ${isEnabled ? styles.sectionEnabled : ''}`}
                    >
                      <div className={styles.sectionItemLeft}>
                        <div
                          className={`${styles.toggle} ${isEnabled ? styles.toggleActive : ''}`}
                          onClick={() => toggleSection(id)}
                        >
                          <div className={styles.toggleKnob} />
                        </div>
                        <div>
                          <span className={styles.sectionLabel}>{label}</span>
                          <span className={styles.sectionDesc}>{SECTION_DESCRIPTIONS[id]}</span>
                        </div>
                      </div>
                      <span className={styles.sectionOrder}>#{(section?.order ?? 0) + 1}</span>
                    </div>
                  );
                })}
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className={styles.tabPanel}>
            {analyticsLoading ? (
              <div className={styles.loading}>Laden...</div>
            ) : !analyticsData ? (
              <div className={styles.previewEmpty}>
                <p>Analytics data kon niet geladen worden.</p>
              </div>
            ) : (
              <>
                {/* KPI cards */}
                <div className={styles.analyticsGrid}>
                  <div className={styles.analyticsCard}>
                    <div className={styles.analyticsCardIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    </div>
                    <div className={styles.analyticsCardValue}>{analyticsData.analytics.totalViews.toLocaleString('nl-NL')}</div>
                    <div className={styles.analyticsCardLabel}>Totaal bezoeken</div>
                  </div>
                  <div className={styles.analyticsCard}>
                    <div className={styles.analyticsCardIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </div>
                    <div className={styles.analyticsCardValue}>{analyticsData.analytics.contactSubmissions}</div>
                    <div className={styles.analyticsCardLabel}>Contactberichten</div>
                  </div>
                  <div className={styles.analyticsCard}>
                    <div className={styles.analyticsCardIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                        <line x1="4" y1="22" x2="4" y2="15" />
                      </svg>
                    </div>
                    <div className={styles.analyticsCardValue}>{analyticsData.analytics.newsletterSignups}</div>
                    <div className={styles.analyticsCardLabel}>Nieuwsbrief aanmeldingen</div>
                  </div>
                  <div className={styles.analyticsCard}>
                    <div className={styles.analyticsCardIcon}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </div>
                    <div className={styles.analyticsCardValue}>
                      {analyticsData.analytics.lastVisitedAt
                        ? new Date(analyticsData.analytics.lastVisitedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                        : 'Geen data'}
                    </div>
                    <div className={styles.analyticsCardLabel}>Laatste bezoek</div>
                  </div>
                </div>

                {/* Chart */}
                <div className={styles.section}>
                  <div className={styles.analyticsChartHeader}>
                    <h3 className={styles.sectionTitle}>Bezoeken per dag</h3>
                    <div className={styles.analyticsPeriodTabs}>
                      {(['7d', '30d', '90d'] as const).map(p => (
                        <button
                          key={p}
                          className={`${styles.analyticsPeriodTab} ${analyticsPeriod === p ? styles.analyticsPeriodActive : ''}`}
                          onClick={() => setAnalyticsPeriod(p)}
                        >
                          {p === '7d' ? '7 dagen' : p === '30d' ? '30 dagen' : '90 dagen'}
                        </button>
                      ))}
                    </div>
                  </div>
                  {(() => {
                    const filteredViews = getFilteredDailyViews();
                    const maxViews = getMaxViews(filteredViews);
                    if (filteredViews.length === 0) {
                      return (
                        <div className={styles.analyticsEmpty}>
                          Nog geen bezoekdata beschikbaar voor deze periode.
                        </div>
                      );
                    }
                    return (
                      <div className={styles.analyticsChart}>
                        <div className={styles.chartYAxis}>
                          <span>{maxViews}</span>
                          <span>{Math.round(maxViews / 2)}</span>
                          <span>0</span>
                        </div>
                        <div className={styles.chartBars}>
                          {filteredViews.map((day: any, i: number) => (
                            <div key={i} className={styles.chartBarWrapper} title={`${day.date}: ${day.views} bezoeken`}>
                              <div
                                className={styles.chartBar}
                                style={{ height: `${(day.views / maxViews) * 100}%` }}
                              />
                              {(analyticsPeriod === '7d' || (analyticsPeriod === '30d' && i % 5 === 0) || (analyticsPeriod === '90d' && i % 15 === 0)) && (
                                <span className={styles.chartLabel}>
                                  {new Date(day.date + 'T00:00:00').toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Recent contacts */}
                {analyticsData.recentContacts && analyticsData.recentContacts.length > 0 && (
                  <div className={styles.section}>
                    <h3 className={styles.sectionTitle}>Recente contactberichten</h3>
                    <div className={styles.analyticsTable}>
                      <div className={styles.analyticsTableHeader}>
                        <span>Naam</span>
                        <span>Bericht</span>
                        <span>Status</span>
                        <span>Datum</span>
                      </div>
                      {analyticsData.recentContacts.map((contact: any) => (
                        <div key={contact._id} className={styles.analyticsTableRow}>
                          <span className={styles.analyticsContactName}>{contact.askedBy?.name || 'Onbekend'}</span>
                          <span className={styles.analyticsContactMsg}>{contact.question?.substring(0, 60)}{contact.question?.length > 60 ? '...' : ''}</span>
                          <span className={`${styles.analyticsStatus} ${styles[`status_${contact.status}`] || ''}`}>
                            {contact.status === 'open' ? 'Open' : contact.status === 'answered' || contact.status === 'beantwoord' ? 'Beantwoord' : contact.status}
                          </span>
                          <span className={styles.analyticsDate}>
                            {new Date(contact.createdAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Newsletter subscribers */}
                <div className={styles.section}>
                  <div className={styles.analyticsChartHeader}>
                    <h3 className={styles.sectionTitle}>Nieuwsbrief abonnees ({subscribers.length})</h3>
                    {subscribers.length > 0 && (
                      <button
                        className={styles.addButton}
                        onClick={() => publicWebsiteAPI.exportSubscribers(project.id)}
                      >
                        Exporteer CSV
                      </button>
                    )}
                  </div>
                  {subscribersLoading ? (
                    <div className={styles.loading}>Laden...</div>
                  ) : subscribers.length === 0 ? (
                    <div className={styles.analyticsEmpty}>
                      Nog geen nieuwsbrief abonnees.
                    </div>
                  ) : (
                    <div className={styles.analyticsTable}>
                      <div className={styles.analyticsTableHeader}>
                        <span>E-mail</span>
                        <span>Aangemeld op</span>
                      </div>
                      {subscribers.map((sub, i) => (
                        <div key={i} className={styles.analyticsTableRow}>
                          <span>{sub.email}</span>
                          <span className={styles.analyticsDate}>
                            {new Date(sub.subscribedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Published info */}
                {analyticsData.analytics.publishedAt && (
                  <div className={styles.analyticsFooter}>
                    Website gepubliceerd op {new Date(analyticsData.analytics.publishedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'preview' && (
          <div className={styles.tabPanel}>
            {settings.enabled && settings.slug ? (
              <div className={styles.previewContainer}>
                <div className={styles.previewToolbar}>
                  <a
                    href={siteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.openButton}
                  >
                    Openen in nieuw tabblad
                  </a>
                </div>
                <iframe
                  src={siteUrl}
                  className={styles.previewFrame}
                  title="Website preview"
                />
              </div>
            ) : (
              <div className={styles.previewEmpty}>
                <p>
                  {!settings.enabled
                    ? 'Activeer de website en stel een URL in om de preview te bekijken.'
                    : 'Stel een URL (slug) in op het tabblad "Algemeen" om de preview te bekijken.'}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
